import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Input } from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import notify from '../../../utils/notify';
import '../../TenderOpening/components/TenderOpening.css';

import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';
import { formatDateTime } from '../../../utils/format';
import StatusBadge from '../../../Components/Common/StatusBadge';

const ActivityLogView = ({ module }) => {
  const token = getAuthToken();
  const { user } = useSelector(s => s.Login);
  const roles = user?.role?.split(',').map(r => r.trim()) || [];
  const isSuperAdmin = roles.includes('Super Admin');

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userNameFilter, setUserNameFilter] = useState('');
  const [debouncedUserName, setDebouncedUserName] = useState('');

  const [sortField, setSortField] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserName(userNameFilter), 500);
    return () => clearTimeout(timer);
  }, [userNameFilter]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const qs = new URLSearchParams();
      if (module) {
        qs.append('Module', module);
      }
      qs.append('PageNumber', currentPage);
      qs.append('PageSize', rowsPerPage);
      qs.append('SortBy', sortField);
      qs.append('IsDescending', sortDirection === 'desc');

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }
      if (dateFrom) {
        qs.append('DateFrom', dateFrom);
      }
      if (dateTo) {
        qs.append('DateTo', dateTo);
      }
      if (
        isSuperAdmin &&
        debouncedUserName &&
        debouncedUserName.trim() !== ''
      ) {
        qs.append('UserName', debouncedUserName.trim());
      }

      const response = await fetch(
        `${api.API_URL}/activity-log?${qs.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 403) {
        notify.error(
          'You do not have access to this page. Contact your administrator if you need access.'
        );
        setData([]);
        setTotalRows(0);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success === false) {
        notify.apiError(result.message, 'load activity log data');
        setData([]);
        setTotalRows(0);
        return;
      }

      setData(result.data?.items || []);
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    module,
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    dateFrom,
    dateTo,
    debouncedUserName,
    sortField,
    sortDirection,
    token,
    isSuperAdmin,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = rows => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    setSortField(column.id);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleFilterChange = setter => e => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchText('');
    setDebouncedSearchText('');
    setCurrentPage(1);
  };

  const clearDateFrom = () => {
    setDateFrom('');
    setCurrentPage(1);
  };

  const clearDateTo = () => {
    setDateTo('');
    setCurrentPage(1);
  };

  const clearUserName = () => {
    setUserNameFilter('');
    setDebouncedUserName('');
    setCurrentPage(1);
  };

  const clearFilters = () => {
    clearSearch();
    clearDateFrom();
    clearDateTo();
    clearUserName();
  };

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: clearSearch,
    },
    dateFrom && {
      key: 'dateFrom',
      label: 'Dari tanggal',
      value: dateFrom,
      onRemove: clearDateFrom,
    },
    dateTo && {
      key: 'dateTo',
      label: 'Sampai tanggal',
      value: dateTo,
      onRemove: clearDateTo,
    },
    isSuperAdmin &&
      userNameFilter && {
        key: 'user',
        label: 'User',
        value: userNameFilter,
        onRemove: clearUserName,
      },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);
  const pageTitle = module ? `Log Aktivitas — ${module}` : 'Log Aktivitas';

  const columns = [
    {
      id: 'createdDate',
      name: 'Waktu',
      selector: row => row.createdDate,
      sortable: true,
      cell: row => <span>{formatDateTime(row.createdDate)}</span>,
      width: '180px',
    },
    {
      id: 'userName',
      name: 'User',
      selector: row => row.userName,
      sortable: true,
      cell: row => (
        <span className='to-title' title={String(row.userName ?? '')}>
          {row.userName || '-'}
        </span>
      ),
    },
    {
      id: 'action',
      name: 'Action',
      selector: row => row.action,
      sortable: true,
      cell: row =>
        row.action ? <StatusBadge tone='info' label={row.action} /> : '-',
    },
    {
      id: 'description',
      name: 'Deskripsi',
      selector: row => row.description,
      sortable: true,
      cell: row => (
        <span className='text-muted' title={String(row.description ?? '')}>
          {row.description || '-'}
        </span>
      ),
      grow: 2,
    },
    {
      id: 'referenceNo',
      name: 'Ref',
      selector: row => row.referenceNo,
      sortable: true,
      cell: row => <span>{row.referenceNo || '-'}</span>,
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb fallback={{ label: 'Activity Log' }} />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-history-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>{pageTitle}</h4>
                      <p className='to-hero-subtitle'>
                        Riwayat aktivitas pengguna pada sistem.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() => fetchData()}
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={searchText}
                      onChange={handleFilterChange(setSearchText)}
                      placeholder='Cari...'
                      ariaLabel='Cari log aktivitas'
                    />
                    <label
                      className={`lf-select${dateFrom ? ' is-active' : ''}`}
                    >
                      <span className='lf-select-label'>
                        {dateFrom && (
                          <span className='lf-dot' aria-hidden='true' />
                        )}
                        Dari tanggal
                      </span>
                      <Input
                        type='date'
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={handleFilterChange(setDateFrom)}
                        aria-label='Dari tanggal'
                      />
                    </label>
                    <label className={`lf-select${dateTo ? ' is-active' : ''}`}>
                      <span className='lf-select-label'>
                        {dateTo && (
                          <span className='lf-dot' aria-hidden='true' />
                        )}
                        Sampai tanggal
                      </span>
                      <Input
                        type='date'
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={handleFilterChange(setDateTo)}
                        aria-label='Sampai tanggal'
                      />
                    </label>
                    {isSuperAdmin && (
                      <label
                        className={`lf-select${userNameFilter ? ' is-active' : ''}`}
                      >
                        <span className='lf-select-label'>
                          {userNameFilter && (
                            <span className='lf-dot' aria-hidden='true' />
                          )}
                          User
                        </span>
                        <Input
                          type='text'
                          placeholder='Filter by user...'
                          value={userNameFilter}
                          onChange={handleFilterChange(setUserNameFilter)}
                          aria-label='Filter by user'
                        />
                      </label>
                    )}
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilters}
                    />
                    <span className='to-toolbar-meta'>
                      {isLoading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} logs`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data}
                      onSort={handleSort}
                      sortServer
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='createdDate'
                      progressPending={isLoading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='Tidak ada log aktivitas'
                          emptyHint={
                            module
                              ? `Belum ada aktivitas yang tercatat untuk modul ${module}. Aktivitas dicatat otomatis saat pengguna bertindak di sistem, jadi log akan muncul di sini setelah ada aktivitas.`
                              : 'Aktivitas dicatat otomatis saat pengguna bertindak di sistem, jadi log akan muncul di sini setelah ada aktivitas.'
                          }
                          filteredHint={`Tidak ada log yang cocok dengan ${filterChips
                            .map(chip => `${chip.label} "${chip.value}"`)
                            .join(
                              ', '
                            )}. Perluas rentang tanggal atau ubah kata kunci pencarian.`}
                          hasFilters={hasFilters}
                          onClear={clearFilters}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={isLoading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ActivityLogView;
